import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getProfile, changePassword } from '../services/api';
import './Profile.css';

const Profile = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await getProfile();
      setProfile(data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError(t('profile.password.errors.allFieldsRequired', 'All fields are required'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('profile.password.errors.passwordsDontMatch', 'New passwords do not match'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('profile.password.errors.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }

    setChangingPassword(true);

    try {
      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );
      setPasswordSuccess(t('profile.password.success', 'Password changed successfully'));
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      const errorMessage = err.message || t('profile.password.errors.failed', 'Failed to change password');
      setPasswordError(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLicenseBadgeClass = (license) => {
    const licenseMap = {
      'free': 'free',
      'basic': 'basic',
      'premium': 'premium',
      'enterprise': 'enterprise'
    };
    return licenseMap[license?.toLowerCase()] || 'free';
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          {t('common.loading', 'Loading...')}
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <p className="profile-kicker">{t('profile.kicker', 'Account')}</p>
        <h1>{t('profile.title', 'Profile')}</h1>
        <p className="profile-lead">{t('profile.subtitle', 'Manage your account settings')}</p>
        <p className="profile-lead-mobile">
          {t('profile.leadMobile', 'Your account details and security.')}
        </p>
      </div>

      <div className="profile-content">
        {/* User Info Card */}
        <div className="profile-card">
          <div className="card-header">
            <div className="user-avatar-large">
              {profile?.username?.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="user-main-info">
              <h2>{profile?.username}</h2>
              <span className="user-email">{profile?.email}</span>
              {profile?.email_verified ? (
                <span className="verified-badge">✓ {t('profile.verified', 'Verified')}</span>
              ) : (
                <span className="unverified-badge">✗ {t('profile.unverified', 'Not Verified')}</span>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">{t('profile.fields.username', 'Username')}</span>
                <span className="info-value">{profile?.username}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('profile.fields.email', 'Email')}</span>
                <span className="info-value">{profile?.email}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('profile.fields.company', 'Company')}</span>
                <span className="info-value">{profile?.company_name || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('profile.fields.license', 'License')}</span>
                <span className={`license-badge ${getLicenseBadgeClass(profile?.license_type)}`}>
                  {profile?.license_type?.toUpperCase() || 'FREE'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('profile.fields.role', 'Role')}</span>
                <span className="info-value">
                  {profile?.is_system_admin 
                    ? t('profile.roles.systemAdmin', 'System Admin')
                    : profile?.is_company_admin 
                    ? t('profile.roles.companyAdmin', 'Company Admin')
                    : t('profile.roles.user', 'User')}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('profile.fields.memberSince', 'Member Since')}</span>
                <span className="info-value">{formatDate(profile?.created_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Card */}
        <div className="profile-card security-card">
          <div className="card-header">
            <h3>🔐 {t('profile.security.title', 'Security')}</h3>
          </div>
          <div className="card-body">
            <div className="security-item">
              <div className="security-info">
                <span className="security-label">{t('profile.security.password', 'Password')}</span>
                <span className="security-hint">{t('profile.security.passwordHint', 'Change your password to keep your account secure')}</span>
              </div>
              <button 
                className="btn-change-password"
                onClick={() => setShowPasswordModal(true)}
              >
                {t('profile.security.changePassword', 'Change Password')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔐 {t('profile.password.title', 'Change Password')}</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="modal-body">
                {passwordError && (
                  <div className="password-error">
                    ❌ {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="password-success">
                    ✅ {passwordSuccess}
                  </div>
                )}

                <div className="form-group">
                  <label>{t('profile.password.current', 'Current Password')}</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>

                <div className="form-group">
                  <label>{t('profile.password.new', 'New Password')}</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>

                <div className="form-group">
                  <label>{t('profile.password.confirm', 'Confirm New Password')}</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowPasswordModal(false)}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={changingPassword}
                >
                  {changingPassword 
                    ? t('common.saving', 'Saving...') 
                    : t('profile.password.save', 'Change Password')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
